import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { GUIDE_IMG, MEDIA } from "@/lib/media";
import MarkdownLite from "@/components/MarkdownLite";
import { fetchGlossaryTerms } from "@/lib/glossaryLink";

export default function Guide() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [terms, setTerms] = useState([]);
  useEffect(() => { api.get(`/guides/${slug}`).then((r) => setG(r.data)).catch(() => setNotFound(true)); }, [slug]);
  useEffect(() => { fetchGlossaryTerms().then(setTerms); }, []);

  if (notFound) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-mutedplum">Guide not found. <Link to="/guides" className="text-teal font-semibold">All guides</Link></div>;
  if (!g) return <div className="py-24 text-center text-mutedplum">Loading…</div>;

  return (
    <div>
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={GUIDE_IMG[g.image_key] || MEDIA.hero} alt={g.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-plum/55" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 h-full flex flex-col justify-end pb-8">
          <nav className="text-cream/70 text-sm flex items-center gap-1.5 mb-2">
            <Link to="/guides" className="hover:text-cream">Guides</Link><ChevronRight className="h-4 w-4" /><span className="text-cream">Guide</span>
          </nav>
          <h1 className="font-display text-3xl sm:text-5xl text-cream leading-tight">{g.title}</h1>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <p className="text-xl text-mutedplum leading-relaxed">{g.subtitle}</p>
        <div className="mt-4 rounded-2xl bg-rose-soft border border-rose/40 p-4 flex items-start gap-2.5" data-testid="guide-disclaimer">
          <ShieldAlert className="h-5 w-5 text-plum mt-0.5 shrink-0" />
          <p className="text-sm text-plum-ink/80">{g.disclaimer}</p>
        </div>
        <div className="mt-8 text-[17px]"><MarkdownLite text={g.body} glossary={terms} /></div>

        {g.faqs?.length > 0 && (
          <section className="mt-12 border-t border-rose/40 pt-8" data-testid="guide-faq">
            <h2 className="font-display text-2xl text-plum mb-5">Frequently asked questions</h2>
            <div className="space-y-4">
              {g.faqs.map((f, i) => (
                <div key={i} className="rounded-2xl bg-white border border-rose/30 p-5 shadow-card" data-testid={`faq-item-${i}`}>
                  <h3 className="font-semibold text-plum text-lg leading-snug">{f.question}</h3>
                  <p className="mt-2 text-[15px] text-mutedplum leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {g.related_guides?.length > 0 && (
          <div className="mt-12 border-t border-rose/40 pt-8">
            <h2 className="font-display text-2xl text-plum mb-4">Keep reading</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {g.related_guides.map((r) => (
                <Link key={r.slug} to={`/guides/${r.slug}`} data-testid={`related-${r.slug}`}
                  className="block bg-white rounded-2xl border border-rose/30 p-5 hover:shadow-card hover:-translate-y-0.5 transition-[transform,box-shadow]">
                  <h3 className="font-display text-lg text-plum">{r.title}</h3>
                  <p className="mt-1 text-sm text-mutedplum line-clamp-2">{r.subtitle}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-3xl bg-teal text-white p-8 text-center grain">
          <h2 className="font-display text-2xl text-white">Have a question about this?</h2>
          <p className="mt-2 text-white/85">Ask the Circle—real women, real experience.</p>
          <Link to="/community" className="mt-5 inline-block rounded-full bg-white px-7 py-3 font-semibold text-teal hover:-translate-y-1 transition-transform duration-300" data-testid="guide-to-community">Go to Community</Link>
        </div>
      </article>
    </div>
  );
}
